import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { FormItem, FormLabel } from '@/Domain/components/Shared/FormCustom/FormCustom';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { SecretInput } from '@/Domain/components/Shared/Input/SecretInput';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import CloseIcon from '@/Domain/components/icons/Close.icon';

const SLACK_INTEGRATION_NAME = 'slack' as const;
const SLACK_QUERY_KEY = [QueryKey.integration.get, SLACK_INTEGRATION_NAME] as const;

const slackLegacySchema = z.object({
  slack_bot_active: z.boolean(),
  slack_app_id: z.string(),
  slack_signing_secret: z.string(),
  slack_bot_user_token: z.string(),
  slack_reply_thread: z.boolean(),
  slack_reply_channel: z.boolean(),
});

type SlackLegacyForm = z.infer<typeof slackLegacySchema>;

interface SlackIntegrationParams {
  slack_bot_active?: boolean;
  slack_app_id?: string | null;
  slack_signing_secret?: string | null;
  slack_bot_user_token?: string | null;
  slack_reply_thread?: boolean;
  slack_reply_channel?: boolean;
}

interface SlackIntegrationResponse {
  name: 'slack';
  params: SlackIntegrationParams;
}

const DEFAULTS: SlackLegacyForm = {
  slack_bot_active: false,
  slack_app_id: '',
  slack_signing_secret: '',
  slack_bot_user_token: '',
  slack_reply_thread: false,
  slack_reply_channel: false,
};

const normalize = (params: SlackIntegrationParams | undefined): SlackLegacyForm => ({
  slack_bot_active: params?.slack_bot_active ?? DEFAULTS.slack_bot_active,
  slack_app_id: params?.slack_app_id ?? DEFAULTS.slack_app_id,
  slack_signing_secret: params?.slack_signing_secret ?? DEFAULTS.slack_signing_secret,
  slack_bot_user_token: params?.slack_bot_user_token ?? DEFAULTS.slack_bot_user_token,
  slack_reply_thread: params?.slack_reply_thread ?? DEFAULTS.slack_reply_thread,
  slack_reply_channel: params?.slack_reply_channel ?? DEFAULTS.slack_reply_channel,
});

interface SlackLegacySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SlackLegacySheet = ({ open, onOpenChange }: SlackLegacySheetProps) => {
  const { t } = useTranslation(['integration']);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<SlackIntegrationResponse>({
    queryKey: SLACK_QUERY_KEY,
    enabled: open,
  });

  const initialValues = useMemo(() => normalize(data?.params), [data]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting },
  } = useForm<SlackLegacyForm>({
    resolver: zodResolver(slackLegacySchema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (data) {
      reset(initialValues);
    }
  }, [data, initialValues, reset]);

  const slackBotActive = watch('slack_bot_active');
  const fieldsDisabled = !slackBotActive;

  const mutation = useMutation<
    SlackIntegrationResponse,
    Error,
    { name: typeof SLACK_INTEGRATION_NAME; params: Partial<SlackLegacyForm> }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (response) => {
      queryClient.setQueryData(SLACK_QUERY_KEY, response);
      toast.success(t('integration:slackLegacy.saved'));
    },
    onError: () => {
      toast.error(t('integration:slackLegacy.save_failed'));
    },
  });

  const onSubmit = handleSubmit((values) => {
    // Trim pasted whitespace from credential fields before diffing so a paste
    // of `"  xoxb-...  "` doesn't get stored verbatim and produce confusing
    // 401s from Slack at runtime (F9 in edge-cases review).
    const trimmedValues: SlackLegacyForm = {
      ...values,
      slack_app_id: values.slack_app_id.trim(),
      slack_signing_secret: values.slack_signing_secret.trim(),
      slack_bot_user_token: values.slack_bot_user_token.trim(),
    };

    const changedFields: Partial<SlackLegacyForm> = {};
    (Object.keys(trimmedValues) as (keyof SlackLegacyForm)[]).forEach((key) => {
      if (trimmedValues[key] !== initialValues[key]) {
        // Re-typed as any because TypeScript can't infer that values[key] is
        // assignable to changedFields[key] when iterating discriminated unions.
        // The keys come from the same shape, so the assignment is safe.
        (changedFields as Record<string, unknown>)[key] = trimmedValues[key];
      }
    });

    if (Object.keys(changedFields).length === 0) {
      toast.success(t('integration:slackLegacy.saved'));
      return;
    }

    mutation.mutate({ name: SLACK_INTEGRATION_NAME, params: changedFields });
  });

  const isBusy = isLoading || mutation.isPending || isSubmitting;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="max-w-3xl w-full h-full" data-testid="slack-legacy-sheet">
        <SheetHeader className="px-0.5">
          <div className="flex flex-col gap-1">
            <SheetTitle data-testid="slack-legacy-title">
              {t('integration:slackLegacy.title')}
            </SheetTitle>
            <SheetDescription data-testid="slack-legacy-description">
              {t('integration:slackLegacy.description')}
            </SheetDescription>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="slack-legacy-close-button"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-6 overflow-y-auto px-0.5 flex-1">
          <Controller
            name="slack_bot_active"
            control={control}
            render={({ field }) => (
              <div className="flex items-center justify-between gap-4 rounded-xl border border-border p-4">
                <Label htmlFor="slack_bot_active" className="cursor-pointer">
                  {t('integration:slackLegacy.enable')}
                </Label>
                <Switch
                  id="slack_bot_active"
                  data-testid="slack_bot_active-switch"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </div>
            )}
          />

          <FormItem>
            <FormLabel htmlFor="slack_app_id">
              {t('integration:slackLegacy.app_id.label')}
            </FormLabel>
            <Controller
              name="slack_app_id"
              control={control}
              render={({ field }) => (
                <Input
                  id="slack_app_id"
                  data-testid="slack_app_id-input"
                  type="text"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('integration:slackLegacy.app_id.placeholder')}
                  disabled={fieldsDisabled}
                />
              )}
            />
          </FormItem>

          <FormItem>
            <FormLabel htmlFor="slack_signing_secret">
              {t('integration:slackLegacy.signing_secret.label')}
            </FormLabel>
            <Controller
              name="slack_signing_secret"
              control={control}
              render={({ field }) => (
                <SecretInput
                  id="slack_signing_secret"
                  data-testid="slack_signing_secret-input"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('integration:slackLegacy.signing_secret.placeholder')}
                  disabled={fieldsDisabled}
                />
              )}
            />
          </FormItem>

          <FormItem>
            <FormLabel htmlFor="slack_bot_user_token">
              {t('integration:slackLegacy.bot_user_token.label')}
            </FormLabel>
            <Controller
              name="slack_bot_user_token"
              control={control}
              render={({ field }) => (
                <SecretInput
                  id="slack_bot_user_token"
                  data-testid="slack_bot_user_token-input"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('integration:slackLegacy.bot_user_token.placeholder')}
                  disabled={fieldsDisabled}
                />
              )}
            />
          </FormItem>

          <Controller
            name="slack_reply_thread"
            control={control}
            render={({ field }) => (
              <div className="flex items-center justify-between gap-4 rounded-xl border border-border p-4">
                <Label htmlFor="slack_reply_thread" className="cursor-pointer">
                  {t('integration:slackLegacy.reply_thread.label')}
                </Label>
                <Switch
                  id="slack_reply_thread"
                  data-testid="slack_reply_thread-switch"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={fieldsDisabled}
                />
              </div>
            )}
          />

          <Controller
            name="slack_reply_channel"
            control={control}
            render={({ field }) => (
              <div className="flex items-center justify-between gap-4 rounded-xl border border-border p-4">
                <Label htmlFor="slack_reply_channel" className="cursor-pointer">
                  {t('integration:slackLegacy.reply_channel.label')}
                </Label>
                <Switch
                  id="slack_reply_channel"
                  data-testid="slack_reply_channel-switch"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={fieldsDisabled}
                />
              </div>
            )}
          />

          <SheetFooter>
            <div className="flex justify-end">
              <Button
                type="submit"
                variant="dark"
                disabled={isBusy}
                data-testid="slack-legacy-save-button"
              >
                {mutation.isPending ? (
                  <Loader2 className="animate-spin size-4" />
                ) : (
                  <Check className="size-4" />
                )}
                {t('integration:slackLegacy.save')}
              </Button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
};
